import type { PermissionAction, PermissionSubject } from '@bliks-io/contracts';
import type { NavMainItem, NavSubItem } from './NavMain';

export type CanFn = (subject: PermissionSubject, action: PermissionAction) => boolean;

function isAllowed(
  item: { requiredPermission?: NavMainItem['requiredPermission'] },
  can: CanFn
): boolean {
  if (!item.requiredPermission) return true;
  return can(item.requiredPermission.subject, item.requiredPermission.action ?? 'read');
}

/**
 * Filters the sidebar navigation down to what the current user may see.
 *
 * - Leaves are visible when their own `requiredPermission` passes.
 * - Groups are only as visible as their children: a group without any visible
 *   sub-item disappears, regardless of its own permission.
 * - A group reduced to a single visible sub-item collapses to a plain link
 *   pointing at that sub-item, unless pinned via `keepGroup` (so its label
 *   stays consistent across roles).
 */
export function filterNavigation(items: NavMainItem[], can: CanFn): NavMainItem[] {
  return items.flatMap((item) => {
    if (!isAllowed(item, can)) return [];

    if (!item.items) return [item];

    const visibleChildren: NavSubItem[] = item.items.filter((sub) => isAllowed(sub, can));

    if (visibleChildren.length === 0) return [];

    if (visibleChildren.length === 1 && !item.keepGroup) {
      const leaf = { ...item, url: visibleChildren[0].url };
      delete leaf.items;
      return [leaf];
    }

    return [{ ...item, items: visibleChildren }];
  });
}
